import { create } from "zustand";

/**
 * The final-questioning token tuples the service has REJECTED, per project
 * (#853).
 *
 * ## WHY THIS IS NOT COMPONENT STATE
 *
 * When the Editor Service refuses a final-questioning write because the tokens
 * the caller presented no longer hold, the surface must not offer the same
 * write again on the same tokens: the retry can only be refused identically,
 * and on the safety-RESTORING direction that is a dead end rather than an
 * inconvenience. The refusal invalidates the read, but an invalidation is not a
 * barrier — TanStack serves the disproven entry until the refetch settles, the
 * same property `validationState`'s `resyncing` member exists for.
 *
 * Held in a store rather than in `FinalQuestioningControl` because the control
 * UNMOUNTS. Safety → Alerts → Safety builds a new one, and a barrier that lived
 * in its state would be gone while the Project header's own copy of the query
 * is still serving the rejected value it holds (claude review round 4). The
 * barrier has to outlive the component that raised it, exactly as the
 * validation outcome outlives the panel that ran it.
 *
 * ## WHY A TUPLE AND NOT A FLAG
 *
 * A flag would need clearing, and every clearing rule is a guess about when the
 * authoritative read landed. The tuple is a fact: the barrier lifts by
 * comparison the moment the served read differs from what was refused, and
 * every refusal this can produce moves one of its coordinates by construction —
 * the state version, the draft revision, the contract revision or the
 * consequences digest. Nothing has to remember to reset it.
 *
 * Keyed by project, and the tuple itself also carries the project id: two
 * untouched Projects share every other coordinate, so a barrier that was
 * neither keyed nor scoped would fire on a Project nobody had touched.
 *
 * No-PHI: opaque revision identities and a digest.
 */
interface FinalQuestioningState {
  /** projectId → the token tuple the service last refused for it. */
  rejected: Readonly<Record<string, string>>;
  /** Record a refusal. Replaces any earlier one for the same project. */
  reject: (projectId: string, tokens: string) => void;
  /**
   * Retire a project's barrier once an authoritative read has observed a
   * different tuple.
   *
   * Without this the entry outlives the session's need for it, and the tuple
   * is not one-way: the consequence wording can change WITHOUT a revision
   * bump, so a rollout and a rollback can produce refused-A, observed-B,
   * observed-A again — and the third reading would re-arm a barrier for a
   * refusal that is long over, with no refresh pending (claude review round 5).
   * The comparison is what makes the barrier correct; retiring is what stops
   * it coming back.
   */
  retire: (projectId: string) => void;
  /** Test seam, and the session reset the store has no product reason to do. */
  reset: () => void;
}

export const useFinalQuestioningStore = create<FinalQuestioningState>()(
  (set) => ({
    rejected: {},
    reject: (projectId, tokens) =>
      set((current) =>
        current.rejected[projectId] === tokens
          ? current
          : { rejected: { ...current.rejected, [projectId]: tokens } },
      ),
    retire: (projectId) =>
      set((current) => {
        if (!Object.hasOwn(current.rejected, projectId)) {
          return current;
        }
        const { [projectId]: _retired, ...rest } = current.rejected;
        return { rejected: rest };
      }),
    reset: () => set({ rejected: {} }),
  }),
);

/**
 * The tuple this project's last refusal named, or null.
 *
 * A selector rather than a whole-store read so a control re-renders when ITS
 * project's barrier moves and not when another project's does.
 */
export function useRejectedFinalQuestioningTokens(
  projectId: string,
): string | null {
  return useFinalQuestioningStore((state) => state.rejected[projectId] ?? null);
}
